import type { ReactNode } from "react";
import { Check } from "lucide-react";
import { useTranslate } from "@refinedev/core";
import { TextareaField } from "@fawkes/ui";
import { StepIntro } from "../pages/WizardModal";

/**
 * The one payment screen for every wizard step where money changes hands — intake (afstandskosten),
 * release (adoptie- en retourkosten) and the intake restitution payout.
 *
 * Its shape is the surrender screen from the design: pick a method, fill the amounts underneath, and
 * watch a running Betalingsoverzicht beside it. The method tiles are shorthand for which amount rows
 * apply — every row stays editable afterwards, so the shorthand never traps anyone. Only the
 * intro line ("… voldoe de afstandskosten") and the totals wording change per flow.
 */

export type PaymentMethodOption = { key: string; label: string; icon: ReactNode };

/** An amount row. `extra` hangs under the row — the payout uses it for the IBAN field. */
export type PaymentAmountRow = { key: string; label: string; icon: ReactNode; extra?: ReactNode };

export type PaymentTotal = { label: string; amount: string };

export type PaymentStepProps = {
  title: string;
  /** The line that names the situation: afstandskosten, adoptiekosten, retourkosten, restitutie. */
  info: string;
  methods: PaymentMethodOption[];
  selectedMethod: string | null;
  onPickMethod: (key: string) => void;
  rows: PaymentAmountRow[];
  values: Record<string, string>;
  amountPlaceholder: string;
  onAmountChange: (key: string, value: string) => void;
  remark: { label: string; placeholder: string; value: string; onChange: (value: string) => void };
  due: PaymentTotal;
  paid: PaymentTotal;
  open: PaymentTotal;
  remaining: PaymentTotal;
  /** Shown under the totals, e.g. "Factuur wordt aangemaakt voor het openstaande bedrag." */
  note?: ReactNode;
  /** Blocking message under the amount rows, e.g. the release "register at least one payment". */
  error?: ReactNode;
};

export function PaymentStep({
  title,
  info,
  methods,
  selectedMethod,
  onPickMethod,
  rows,
  values,
  amountPlaceholder,
  onAmountChange,
  remark,
  due,
  paid,
  open,
  remaining,
  note,
  error,
}: PaymentStepProps) {
  const t = useTranslate();
  return (
    <div className="fawkes-inboeken__step fawkes-pay">
      <StepIntro title={title} info={info} />
      <div className="fawkes-pay__columns">
        <section className="fawkes-pay__main">
          <h4 className="fawkes-pay__legend">{t("inboeken.payment.step1")}</h4>
          <div className="fawkes-pay__methods">
            {methods.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`fawkes-pay__method${selectedMethod === m.key ? " is-selected" : ""}`}
                onClick={() => onPickMethod(m.key)}
              >
                {selectedMethod === m.key && (
                  <Check className="fawkes-pay__method-check" size={14} aria-hidden="true" />
                )}
                {m.icon}
                <span>{m.label}</span>
              </button>
            ))}
          </div>
          <h4 className="fawkes-pay__legend">{t("inboeken.payment.step2")}</h4>
          <div className="fawkes-pay__rows">
            {rows.map((r) => (
              <div className="fawkes-pay__row-group" key={r.key}>
                <label className="fawkes-pay__row">
                  <span className="fawkes-pay__row-label">{r.icon}{r.label}</span>
                  <span className="fawkes-pay__row-amount">
                    <span aria-hidden="true">€</span>
                    <input
                      className="fui-field__input"
                      type="text"
                      inputMode="decimal"
                      aria-label={r.label}
                      value={values[r.key] ?? ""}
                      placeholder={amountPlaceholder}
                      onChange={(e) => onAmountChange(r.key, e.target.value)}
                    />
                  </span>
                </label>
                {r.extra}
              </div>
            ))}
          </div>
          {error && <span className="fawkes-inboeken__field-error">{error}</span>}
          <TextareaField
            label={remark.label}
            value={remark.value}
            rows={2}
            placeholder={remark.placeholder}
            onChange={(e) => remark.onChange(e.target.value)}
          />
        </section>
        <aside className="fawkes-pay__overview">
          <span className="fawkes-terms__tag">{t("inboeken.payment.overview")}</span>
          <dl className="fawkes-pay__totals">
            <div>
              <dt>{due.label}</dt>
              <dd>{due.amount}</dd>
            </div>
            <div>
              <dt>{paid.label}</dt>
              <dd className="fawkes-pay__paid">{paid.amount}</dd>
            </div>
            <div>
              <dt>{open.label}</dt>
              <dd className="fawkes-pay__open">{open.amount}</dd>
            </div>
          </dl>
          {note && <p className="fawkes-pay__note">{note}</p>}
          <div className="fawkes-pay__remaining">
            <Check size={16} aria-hidden="true" />
            <span>
              {remaining.label}
              <strong>{remaining.amount}</strong>
            </span>
          </div>
        </aside>
      </div>
    </div>
  );
}
